import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	redirect,
	useNavigate,
	useParams,
	useRouterState,
	useSearch,
} from "@tanstack/react-router";
import { Calendar, CalendarCheck01 } from "@untitledui/icons";
import { useEffect, useState } from "react";
import z from "zod";
import { DeleteTicketTypeModal } from "@/components/Organisms/DeleteTicketTypeModal/DeleteTicketTypeModal";
import type { DrawerHeaderMetaItem } from "@/components/Organisms/DrawerHeader/DrawerHeader";
import { DuplicatingEntityTypeModal } from "@/components/Organisms/DuplicatingEntityTypeModal/DuplicatingEntityTypeModal";
import { EMPTY_ORG_ID } from "@/constants/org";
import type { EntityTypeDetailsPatch } from "@/features/entity-types/components/entity-type-details-panel";
import { EntityTypeDrawer } from "@/features/entity-types/components/entity-type-drawer";
import { useDuplicateEntityType } from "@/features/entity-types/hooks/use-duplicate-entity-type";
import { useSyncedLocationRequired } from "@/features/entity-types/hooks/use-synced-location-required";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { authQueryOptions } from "@/services/auth/auth.options";
import {
	useDuplicateTicketType,
	useUpdateTicketType,
} from "@/services/ticket/ticket.mutations";
import { ticketQueryOptions } from "@/services/ticket/ticket.options";
import {
	canShowActivityTab,
	getTicketTypeDisplayName,
} from "@/utils/ticket-type-detail/ticket-type-detail.utils";
import {
	TicketTypeDrawerContext,
	type TicketTypeDrawerContextValue,
} from "@/utils/ticket-type-detail/ticket-type-drawer-context";
import {
	createdMetadataLabel,
	updatedMetadataLabel,
} from "@/utils/entity-metadata-date-label";
import { showSuccessToast } from "@/utils/toast.utils";

const SearchSchema = z.object({
	mode: z.enum(["view", "edit"]).optional().default("view"),
	presentation: z
		.enum(["drawer", "modal", "full"])
		.optional()
		.default("drawer"),
});

type TicketTypeTab = "details" | "schema" | "lifecycle" | "rules" | "activity";

function getCurrentTab(pathname: string): TicketTypeTab {
	const segment = pathname.split("/").pop();
	if (
		segment === "details" ||
		segment === "schema" ||
		segment === "lifecycle" ||
		segment === "rules" ||
		segment === "activity"
	) {
		return segment;
	}
	return "details";
}

export const Route = createFileRoute(
	"/_authenticated/(admin-app)/_admin/admin/configuration/ticketTypes/$ticketTypeId",
)({
	component: AdminTicketTypeIdLayout,
	params: {
		parse: (params) => ({
			ticketTypeId: z.string().parse(params.ticketTypeId),
		}),
	},
	validateSearch: (search) => SearchSchema.parse(search),
	beforeLoad: ({ location, params, search }) => {
		const normalized = location.pathname.replace(/\/$/, "");
		const exactRoot = normalized.endsWith(
			`/configuration/ticketTypes/${params.ticketTypeId}`,
		);
		if (exactRoot) {
			const parsed = SearchSchema.safeParse(search);
			const s = parsed.success ? parsed.data : SearchSchema.parse({});
			throw redirect({
				to: "/admin/configuration/ticketTypes/$ticketTypeId/details",
				params: { ticketTypeId: params.ticketTypeId },
				search: {
					mode: s.mode,
					presentation: s.presentation,
				},
			});
		}
	},
});

function AdminTicketTypeIdLayout() {
	const { t } = useAppTranslation();
	const navigate = useNavigate();
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const isFormRoute = useRouterState({
		select: (s) =>
			s.matches.some(
				(m) =>
					typeof m.routeId === "string" &&
					m.routeId.endsWith("/$ticketTypeId/form"),
			),
	});
	const params = useParams({
		from: "/_authenticated/(admin-app)/_admin/admin/configuration/ticketTypes/$ticketTypeId",
	});
	const search = useSearch({
		from: "/_authenticated/(admin-app)/_admin/admin/configuration/ticketTypes/$ticketTypeId",
	});

	const isFullRoute = /\/configuration\/ticketTypes\/[^/]+\/full/.test(
		pathname,
	);

	const [isOpen, setIsOpen] = useState(() => !isFullRoute);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);

	const detailQuery = useQuery(
		ticketQueryOptions.ticketTypeDetail(params.ticketTypeId),
	);
	const canDeleteQuery = useQuery({
		...authQueryOptions.checkAction({
			name: "DeleteTicketType",
			entityType: "TicketType",
			entityId: params.ticketTypeId,
		}),
	});
	const canEditQuery = useQuery({
		...authQueryOptions.checkAction({
			name: "EditTicketType",
			entityType: "TicketType",
			entityId: params.ticketTypeId,
		}),
	});
	const isPlatformAdminQuery = useQuery(
		authQueryOptions.checkAction({
			name: "ViewAllOrganizations",
			entityType: null,
			entityId: null,
		}),
	);
	const isPlatformAdmin = isPlatformAdminQuery.data ?? false;

	const updateMutation = useUpdateTicketType();
	const duplicateMutation = useDuplicateTicketType();
	const { duplicate: duplicateTicketType, isPending: isDuplicating } =
		useDuplicateEntityType({
			mutation: duplicateMutation,
			toVariables: (ticketTypeId) => ({
				ticketTypeId,
				organizationId: isPlatformAdmin ? EMPTY_ORG_ID : undefined,
			}),
			onDuplicated: (ticketTypeId) => {
				void navigate({
					to: "/admin/configuration/ticketTypes/$ticketTypeId/details",
					params: { ticketTypeId },
					search: { mode: "view", presentation: "full" },
				});
			},
			errorMessage: t("admin.ticketTypes.modal.createError"),
		});

	const ticketType = detailQuery.data ?? null;
	const [locationRequired, setLocationRequired] = useSyncedLocationRequired(
		ticketType?.id,
		ticketType?.locationRequired,
	);

	const [editedName, setEditedName] = useState("");
	const [editedDescription, setEditedDescription] = useState("");
	const [editedIcon, setEditedIcon] = useState("");
	const [draftName, setDraftName] = useState("");
	const [draftDescription, setDraftDescription] = useState("");
	const [draftIcon, setDraftIcon] = useState("");

	useEffect(() => {
		if (!ticketType) return;
		const name = getTicketTypeDisplayName(ticketType);
		const description = ticketType.description ?? "";
		const icon = ticketType.icon ?? "";
		if (search.mode !== "edit" || draftName === editedName) {
			setEditedName(name);
			setDraftName(name);
		}
		if (search.mode !== "edit" || draftDescription === editedDescription) {
			setEditedDescription(description);
			setDraftDescription(description);
		}
		if (search.mode !== "edit" || draftIcon === editedIcon) {
			setEditedIcon(icon);
			setDraftIcon(icon);
		}
	}, [ticketType]);

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(() => {
			void navigate({
				to: "/admin/configuration/ticketTypes",
				replace: true,
			});
		}, 200);
	};

	const handleOpenChange = (open: boolean) => {
		setIsOpen(open);
		if (!open) handleClose();
	};

	if (isFormRoute) {
		return (
			<div className="flex min-h-0 flex-1 flex-col bg-background-primary">
				<Outlet />
			</div>
		);
	}

	const isDetailLoading = detailQuery.isLoading;
	const isDetailError =
		!isDetailLoading && (detailQuery.isError || ticketType === null);
	const isDetailReady =
		!isDetailLoading && !detailQuery.isError && ticketType !== null;

	const loadedTicketType = ticketType ?? undefined;
	const displayTicketType =
		loadedTicketType && isDetailReady
			? {
					...loadedTicketType,
					name: editedName,
					description: editedDescription,
					icon: editedIcon,
				}
			: null;

	const canEdit = !!loadedTicketType && isDetailReady && !!canEditQuery.data;
	const showActivity =
		!!loadedTicketType &&
		isDetailReady &&
		canShowActivityTab("platform", loadedTicketType);
	const currentTab = getCurrentTab(pathname);

	const isEditing = search.mode === "edit";

	const tabs: { id: TicketTypeTab; label: string }[] = isDetailReady
		? [
				{ id: "details", label: t("admin.ticketTypes.drawer.tabs.details") },
				{ id: "schema", label: t("admin.ticketTypes.drawer.tabs.form") },
				{
					id: "lifecycle",
					label: t("admin.ticketTypes.drawer.tabs.lifecycle"),
				},
				{ id: "rules", label: t("admin.ticketTypes.drawer.tabs.rules") },
				...(showActivity
					? [
							{
								id: "activity" as const,
								label: t("admin.ticketTypes.drawer.tabs.activity"),
							},
						]
					: []),
			]
		: [];

	const currentTabInfo = tabs.find((tab) => tab.id === currentTab);
	const editModeTabs = currentTabInfo
		? [
				{
					id: currentTabInfo.id,
					label: `Edit ${currentTabInfo.label}`,
				},
			]
		: [];

	const canDelete =
		!!canDeleteQuery.data && isDetailReady && search.mode !== "edit";
	const canDuplicate = isDetailReady && search.mode !== "edit";

	const isSaving = updateMutation.isPending;
	const isValid = draftName.trim() !== "";
	const isDirty =
		draftName !== editedName ||
		draftDescription !== editedDescription ||
		draftIcon !== editedIcon;

	const saveDraftFn = async (): Promise<void> => {
		if (!loadedTicketType || !isValid) return;
		try {
			await updateMutation.mutateAsync({
				ticketTypeId: loadedTicketType.id,
				data: {
					name: draftName.trim(),
					description: draftDescription || null,
					icon: draftIcon || null,
					typeId: loadedTicketType.id,
					statusLifecycleId: loadedTicketType.lifecycleId ?? null,
					locationRequired,
				},
				organizationId: EMPTY_ORG_ID,
			});
			setEditedName(draftName.trim());
			setEditedDescription(draftDescription);
			setEditedIcon(draftIcon);
			showSuccessToast({
				message: t("admin.ticketTypes.drawer.details.toast.saveSuccess"),
			});
			void navigate({
				to: `/admin/configuration/ticketTypes/${params.ticketTypeId}/details`,
				search: { mode: "view" },
			});
		} catch {
			// Global mutation error handler in root-provider.tsx already shows the error toast
		}
	};

	const cancelDraftFn = () => {
		setDraftName(editedName);
		setDraftDescription(editedDescription);
		setDraftIcon(editedIcon);
		void navigate({
			to: `/admin/configuration/ticketTypes/${params.ticketTypeId}/details`,
			search: { mode: "view" },
		});
	};

	const persistDetails = async (
		patch: EntityTypeDetailsPatch,
	): Promise<void> => {
		if (!loadedTicketType) return;
		const name = (patch.name ?? editedName).trim();
		const description =
			patch.description !== undefined
				? (patch.description ?? "")
				: editedDescription;
		const icon = patch.icon !== undefined ? (patch.icon ?? "") : editedIcon;
		await updateMutation.mutateAsync({
			ticketTypeId: loadedTicketType.id,
			data: {
				name,
				description: description || null,
				icon: icon || null,
				typeId: loadedTicketType.id,
				statusLifecycleId: loadedTicketType.lifecycleId ?? null,
				locationRequired,
			},
			organizationId: EMPTY_ORG_ID,
		});
		setEditedName(name);
		setDraftName(name);
		setEditedDescription(description);
		setDraftDescription(description);
		setEditedIcon(icon);
		setDraftIcon(icon);
	};

	const persistLocationRequired = async (next: boolean): Promise<void> => {
		if (!loadedTicketType) return;
		await updateMutation.mutateAsync({
			ticketTypeId: loadedTicketType.id,
			data: {
				name: (editedName || loadedTicketType.name || "").trim(),
				description: editedDescription || loadedTicketType.description || null,
				icon: editedIcon || loadedTicketType.icon || null,
				typeId: loadedTicketType.id,
				statusLifecycleId: loadedTicketType.lifecycleId ?? null,
				locationRequired: next,
			},
			organizationId: EMPTY_ORG_ID,
		});
		setLocationRequired(next);
	};

	const contextValue: TicketTypeDrawerContextValue | null =
		loadedTicketType && isDetailReady && displayTicketType
			? {
					scope: "platform",
					ticketType: displayTicketType,
					mode: search.mode,
					setMode: (mode) => {
						void navigate({
							to: `/admin/configuration/ticketTypes/${params.ticketTypeId}/details`,
							search: { mode },
						});
					},
					canEdit,
					draftName,
					draftDescription,
					draftIcon,
					setDraftName,
					setDraftDescription,
					setDraftIcon,
					draftSubOrgIds: null,
					setDraftSubOrgIds: () => {},
					isSaving,
					isValid,
					isDirty,
					saveDraft: saveDraftFn,
					cancelDraft: cancelDraftFn,
					persistDetails,
					locationRequired,
					persistLocationRequired,
				}
			: null;

	const headerTitle =
		displayTicketType !== null
			? getTicketTypeDisplayName(displayTicketType)
			: t("admin.ticketTypes.drawer.header.titlePlaceholder");

	const drawerMetadata: DrawerHeaderMetaItem[] = [];
	if (loadedTicketType?.createdAt) {
		drawerMetadata.push({
			id: "created",
			label: createdMetadataLabel(t, loadedTicketType.createdAt),
			icon: Calendar,
		});
	}
	if (loadedTicketType?.updatedAt) {
		drawerMetadata.push({
			id: "updated",
			label: updatedMetadataLabel(t, loadedTicketType.updatedAt),
			icon: CalendarCheck01,
		});
	}

	if (isFullRoute) {
		return contextValue ? (
			<TicketTypeDrawerContext.Provider value={contextValue}>
				<Outlet />
			</TicketTypeDrawerContext.Provider>
		) : (
			<Outlet />
		);
	}

	// useRouterState updates before the component unmounts, so during navigation
	// away from any $ticketTypeId route the pathname briefly shows the new URL.
	// Guard against mounting the Drawer during that window.
	const isOnTicketTypeSubroute = /\/configuration\/ticketTypes\/[^/]+\//.test(
		pathname,
	);

	if (!isOnTicketTypeSubroute) {
		return null;
	}

	return (
		<>
			<EntityTypeDrawer
				type="ticket"
				isOpen={isOpen}
				onOpenChange={handleOpenChange}
				title={headerTitle}
				metadata={drawerMetadata}
				workspaceLabel={t("main.jobs.workspace.action")}
				showWorkspace={isDetailReady}
				onWorkspace={() => {
					void navigate({
						to: `/admin/configuration/ticketTypes/${params.ticketTypeId}/full`,
						search: { tab: "details", mode: "view" },
					});
				}}
				duplicateLabel={
					canDuplicate
						? t("admin.ticketTypes.table.actions.duplicate")
						: undefined
				}
				onDuplicate={
					canDuplicate
						? () => duplicateTicketType(params.ticketTypeId)
						: undefined
				}
				deleteLabel={
					canDelete
						? t("admin.ticketTypes.drawer.footer.deleteTicketType")
						: undefined
				}
				onDelete={canDelete ? () => setDeleteModalOpen(true) : undefined}
				isLoading={isDetailLoading}
				isError={isDetailError}
				errorTitle={t("admin.ticketTypes.drawer.errors.loadFailed")}
				errorDescription={t("admin.ticketTypes.drawer.errors.notFound")}
				tabs={isEditing ? editModeTabs : tabs}
				currentTab={currentTab}
				onTabChange={(tabId) =>
					void navigate({
						to: `/admin/configuration/ticketTypes/${params.ticketTypeId}/${tabId}`,
						search: { mode: search.mode },
					})
				}
				data-testid="ticket-type-drawer"
			>
				{isDetailReady && contextValue ? (
					<TicketTypeDrawerContext.Provider value={contextValue}>
						<Outlet />
					</TicketTypeDrawerContext.Provider>
				) : null}
			</EntityTypeDrawer>

			<DeleteTicketTypeModal
				isOpen={deleteModalOpen}
				setIsOpen={setDeleteModalOpen}
				handleClose={() => setDeleteModalOpen(false)}
				onDeleteSuccess={() => {
					void navigate({ to: "/admin/configuration/ticketTypes" });
				}}
				ticketTypeId={params.ticketTypeId}
				ticketTypeName={ticketType ? getTicketTypeDisplayName(ticketType) : ""}
				organizationId={isPlatformAdmin ? EMPTY_ORG_ID : undefined}
			/>

			<DuplicatingEntityTypeModal
				isOpen={isDuplicating}
				label={t("admin.ticketTypes.duplicate.inProgress")}
				testId="tickettype-drawer-duplicate-in-progress"
			/>
		</>
	);
}
